import { forwardRef, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import type { LearningMaterial } from '@common/domain/learning-workspace';
import type { Persona } from '../../lib/api/Persona';
import { LearningMaterialMarkdown } from './LearningMaterialMarkdown';

export interface LearningTeachingRegionProps {
  persona: Persona;
  material: LearningMaterial;
  conversationSessionId?: string;
  conversationBoundaryKey?: string;
  studySessionId?: string;
  allowNewChat?: boolean;
  onNewChat?: () => void;
  onTeachingInteraction?: () => void;
  onChatSessionIdentified?: (chatId: string) => boolean | void | Promise<boolean | void>;
  onConversationIdentified?: (conversationId: string) => boolean | void | Promise<boolean | void>;
  onTranscriptVisibilityChange?: (visible: boolean) => void;
}

export const TextbookTeachingRegion = forwardRef<HTMLHeadingElement, LearningTeachingRegionProps>(
  function TextbookTeachingRegion({ material, onTeachingInteraction }, headingRef) {
    const pointerFocusRef = useRef(false);
    return (
      <Box
        aria-label={`Curriculum guide for ${material.title}`}
        className="learning-workspace-page__teaching-region"
        onFocusCapture={() => {
          if (!pointerFocusRef.current) onTeachingInteraction?.();
        }}
        onKeyDown={onTeachingInteraction}
        onPointerCancel={() => { pointerFocusRef.current = false; }}
        onPointerDown={() => {
          pointerFocusRef.current = true;
          onTeachingInteraction?.();
        }}
        onPointerUp={() => { pointerFocusRef.current = false; }}
        onWheel={onTeachingInteraction}
        tabIndex={0}
      >
        <Typography component="h2" ref={headingRef} tabIndex={-1} variant="h5">
          Curriculum guide
        </Typography>
        {material.curriculumGuideMarkdown ? (
          <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: 1 }}>
            <LearningMaterialMarkdown contentMarkdown={material.curriculumGuideMarkdown} />
          </Box>
        ) : (
          <Typography color="text.secondary">Curriculum guide is unavailable for this chapter.</Typography>
        )}
      </Box>
    );
  },
);
